/**
 * 项目管理列表的搜索 / 筛选 / 排序 / 页码与 URL query 双向绑定：
 * 进详情再返回、刷新、浏览器后退都回到同一组条件。手改 URL 的非法值回落默认，不把坏参数发给服务端。
 */
import { LANDING_PROJECT_DESENSITIZATION_FILTERS, LANGUAGE_CODE_RE } from '@novel-admin/shared';
import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ProjectDesensitizationFilter, ProjectSort } from '~/hooks/use-landing-projects';

const PROJECT_SORTS: ProjectSort[] = ['updated_desc', 'created_desc', 'name_asc'];
const DESENSITIZATION_FILTERS: readonly ProjectDesensitizationFilter[] =
  LANDING_PROJECT_DESENSITIZATION_FILTERS;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface LandingProjectListQuery {
  search: string;
  /** 默认只看使用中（URL 不带 status）；清空筛选 = 全部，URL 记为 status=all。 */
  status: string[];
  product: string[];
  language: string[];
  /** 按已加入投放章节的 AI 脱敏状态筛选，单选；空 = 不筛。 */
  desensitization: ProjectDesensitizationFilter[];
  sort: ProjectSort;
  page: number;
}

export function parseLandingProjectListQuery(params: URLSearchParams): LandingProjectListQuery {
  const status = params.get('status');
  const product = params.get('product') ?? '';
  const language = params.get('language') ?? '';
  const desensitization = params.get('desensitization') as ProjectDesensitizationFilter | null;
  const sort = params.get('sort') as ProjectSort | null;
  const page = Number(params.get('page'));
  return {
    // 服务端 search 上限 256，超长截断而不是让整页 422。
    search: (params.get('q') ?? '').slice(0, 256),
    status:
      status === 'all' ? [] : status === 'ARCHIVED' || status === 'ACTIVE' ? [status] : ['ACTIVE'],
    product: UUID_RE.test(product) ? [product] : [],
    language: LANGUAGE_CODE_RE.test(language) ? [language] : [],
    desensitization:
      desensitization && DESENSITIZATION_FILTERS.includes(desensitization) ? [desensitization] : [],
    sort: sort && PROJECT_SORTS.includes(sort) ? sort : 'updated_desc',
    page: Number.isInteger(page) && page > 1 ? page : 1,
  };
}

/** 只写非默认值，默认条件下 URL 保持干净的 /landing/projects。 */
export function writeLandingProjectListQuery(
  params: URLSearchParams,
  patch: Partial<LandingProjectListQuery>,
) {
  const next = new URLSearchParams(params);
  const put = (key: string, value: string | undefined) => {
    if (value) next.set(key, value);
    else next.delete(key);
  };
  if (patch.search !== undefined) put('q', patch.search);
  if (patch.status !== undefined)
    put(
      'status',
      patch.status.length === 0
        ? 'all'
        : patch.status[0] === 'ACTIVE'
          ? undefined
          : patch.status[0],
    );
  if (patch.product !== undefined) put('product', patch.product[0]);
  if (patch.language !== undefined) put('language', patch.language[0]);
  if (patch.desensitization !== undefined) put('desensitization', patch.desensitization[0]);
  if (patch.sort !== undefined) put('sort', patch.sort === 'updated_desc' ? undefined : patch.sort);
  if (patch.page !== undefined) put('page', patch.page > 1 ? String(patch.page) : undefined);
  return next;
}

export function useLandingProjectListQuery() {
  const [searchParams, setSearchParams] = useSearchParams();
  // setSearchParams 的函数式更新读的是本次渲染的 params：同一事件里连写两次（如改筛选再回第 1 页）
  // 后一次会覆盖前一次。经 ref 串起来，连续写入按顺序叠加。
  // 只在路由真的交来新 params 时同步 ref：URL 更新在 transition 里挂起期间，若组件以旧 params
  // 重渲染，不能把尚未提交的写入覆盖回旧值。
  const latest = useRef(searchParams);
  const seen = useRef(searchParams);
  if (seen.current !== searchParams) {
    seen.current = searchParams;
    latest.current = searchParams;
  }
  const update = useCallback(
    (patch: Partial<LandingProjectListQuery>) => {
      const next = writeLandingProjectListQuery(latest.current, patch);
      latest.current = next;
      // replace：筛选变化不堆浏览历史，后退直接离开列表或回到上一页面。
      setSearchParams(next, { replace: true });
    },
    [setSearchParams],
  );
  return [parseLandingProjectListQuery(searchParams), update] as const;
}
